import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// Replaces the previous global-only `EditorRenderEpochProvider` counter.
// `globalVersion` still covers whole-document remounts (discard, hydrate);
// `byOwnerId` lets a single block's editor instance (see
// `resolveEditorOwnerId`) remount on its own, without forcing every other
// editor on the page to remount too.
export type EditorRemountState = {
  globalVersion: number;
  byOwnerId: Record<string, number>;
};

const initialState: EditorRemountState = {
  globalVersion: 0,
  byOwnerId: {},
};

const editorRemountSlice = createSlice({
  name: 'editorRemount',
  initialState,
  reducers: {
    // Whole-document remount — e.g. discarding local edits or re-hydrating
    // from a freshly-loaded/saved server tree, where every editor instance
    // needs to resync from the new Redux state.
    bumpGlobalRemount(state) {
      state.globalVersion += 1;
    },
    // Scoped remount for a single owner (see `resolveEditorOwnerId`) — e.g.
    // a reference create/remove/token-conversion that rewrote one source
    // block's text. Leaves every other editor instance untouched.
    bumpOwnerRemount(state, action: PayloadAction<string>) {
      const ownerId = action.payload;
      state.byOwnerId[ownerId] = (state.byOwnerId[ownerId] ?? 0) + 1;
    },
  },
});

export const { bumpGlobalRemount, bumpOwnerRemount } =
  editorRemountSlice.actions;

export default editorRemountSlice.reducer;

/**
 * Effective remount key for a given owner — combines both the global and
 * owner-specific counters so consumers pick up EITHER kind of bump. Always
 * combine explicitly like this; never do a `byOwnerId[id] ?? globalVersion`
 * fallback lookup, which would make an owner-scoped bump invisible to
 * consumers once that owner has its own counter entry (and vice versa).
 */
export function selectEditorRemountKey(
  state: EditorRemountState,
  ownerId: string,
): string {
  const ownerVersion = state.byOwnerId[ownerId] ?? 0;
  return `${String(state.globalVersion)}:${String(ownerVersion)}`;
}
